import {makeAutoObservable} from 'mobx';
import LoadingModule from '../../../../modules/LoadingModule/LoadingModule';
import MicrolessonsRepository from '../../../../data/repositories/MicrolessonsRepository';
import {LessonGenerationFromPromptModel} from '../../../../data/models/AI/LessonGenerationModel';
import {toast} from 'react-toastify';
import {getErrorFromCode} from '../../../../data/errorCodes';
import {toastConfig} from '../../../../uiToolkit/Toast/toastConfig';
import {generateMicrolessonsText} from '../generateMicrolessonsText';
import {MicrolessonGenerationCostModel} from '../../../../data/models/Microlessons/MicrolessonGenerationCostModel';

class GenerateMicrolessonStore {
  private loadingModule = new LoadingModule();
  private microlessonsRepo = new MicrolessonsRepository();
  private aiEstimationLoading = new LoadingModule();

  constructor() {
    makeAutoObservable(this);
  }

  public get isEstimationLoading() {
    return this.aiEstimationLoading.isLoading;
  }

  public get isLoading() {
    return this.loadingModule.isLoading;
  }

  public async generateLessonFromPrompt(
    organizationId: string,
    model: LessonGenerationFromPromptModel,
  ) {
    this.loadingModule.startLoading();
    const result = await this.microlessonsRepo.genrateLessonFromPromptWithAI(
      organizationId,
      model,
    );
    this.loadingModule.endLoading();
    if (!result.success && result.errors && result.errors[0]) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    if (result.success) {
      toast.success(generateMicrolessonsText.generationStarted, toastConfig);
    }
    return result.success;
  }

  public async generateLessonFromVideo(
    organizationId: string,
    includeBranch: boolean,
    includeGrading: boolean,
    contentId: string,
    language: string,
    dialect: string,
    name: string,
    passingScore?: number,
    mediaType: 'video' | 'document' = 'video',
  ) {
    this.loadingModule.startLoading();

    const result =
      mediaType === 'document'
        ? await this.microlessonsRepo.generateLessonFromSlide(organizationId, {
            libraryMediaFileId: contentId,
            addBranching: includeBranch,
            addAssessment: includeGrading,
            passingScore,
            language,
            dialect,
            name,
          })
        : await this.microlessonsRepo.generateLesson(organizationId, {
            contentId,
            includeChoiceBranch: includeBranch,
            isGradable: includeGrading,
            passingScore,
            language,
            name,
            dialect,
          });

    this.loadingModule.endLoading();

    if (result.errors?.length) {
      toast.error(getErrorFromCode(result.errors[0]), {
        ...toastConfig,
        autoClose: 8000,
      });
    } else if (result.success) {
      const successMessage =
        !includeBranch && !includeGrading
          ? generateMicrolessonsText.lessonIsGenerated
          : generateMicrolessonsText.lessonIsBeingGenerated;

      toast.success(successMessage, {
        ...toastConfig,
        autoClose: 8000,
      });
    }

    return result.success;
  }

  public async getMicrolessonGenerationCost(
    organizationId: string,
    model: MicrolessonGenerationCostModel,
  ) {
    this.aiEstimationLoading.startLoading();
    const result = await this.microlessonsRepo.getMicrolessonCostEstimation(
      organizationId,
      model,
    );
    this.aiEstimationLoading.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
    return {
      availableCredits: 0,
      estimatedCost: 1,
    };
  }

  public async checkGenerationAvailability(
    organizationId: string,
  ): Promise<boolean> {
    this.loadingModule.startLoading();
    const result =
      await this.microlessonsRepo.checkGenerationAvailability(organizationId);
    this.loadingModule.endLoading();
    if (!result.success && result.errors && result.errors[0]) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
      return false;
    }
    if (result.success) {
      return result.data ?? false;
    }
    return false;
  }
}

export default GenerateMicrolessonStore;
